import { useEffect, useState } from 'react';

interface TechAnimationProps {
  type: 'data-flow' | 'network-pulse' | 'circuit-glow' | 'binary-rain' | 'loading-dots';
  className?: string;
  children?: React.ReactNode;
  delay?: number;
}

export const TechAnimation = ({ type, className = '', children, delay = 0 }: TechAnimationProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const animationClasses = {
    'data-flow': 'animate-data-flow',
    'network-pulse': 'animate-network-pulse',
    'circuit-glow': 'animate-circuit-glow', 
    'binary-rain': 'animate-binary-rain',
    'loading-dots': 'animate-loading-dots'
  };

  return (
    <div className={`${isVisible ? animationClasses[type] : ''} ${className}`}>
      {children}
    </div>
  );
};

interface TeamAnimationProps {
  type: 'team-connect' | 'sync-bounce' | 'collaboration-orbit' | 'team-formation' | 'knowledge-share';
  className?: string;
  children?: React.ReactNode;
  delay?: number;
}

export const TeamAnimation = ({ type, className = '', children, delay = 0 }: TeamAnimationProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const animationClasses = {
    'team-connect': 'animate-team-connect',
    'sync-bounce': 'animate-sync-bounce',
    'collaboration-orbit': 'animate-collaboration-orbit',
    'team-formation': 'animate-team-formation',
    'knowledge-share': 'animate-knowledge-share'
  };

  return (
    <div className={`${isVisible ? animationClasses[type] : ''} ${className}`}>
      {children}
    </div>
  );
};

// Code typing animation component
export const CodeTypingAnimation = ({ 
  text = "Building innovative solutions...", 
  className = "",
  speed = 100 
}: { 
  text?: string; 
  className?: string;
  speed?: number;
}) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, text, speed]);

  return (
    <div className={`font-mono text-primary ${className}`}>
      {displayText}
      <span className="animate-pulse">|</span>
    </div>
  );
};

// Network connection lines animation
export const NetworkLines = ({ className = "" }: { className?: string }) => {
  return (
    <svg className={`absolute inset-0 pointer-events-none ${className}`} width="100%" height="100%">
      <defs>
        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="transparent" />
          <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
          <stop offset="100%" stopColor="transparent" />
        </linearGradient>
      </defs>
      <line
        x1="0%"
        y1="20%"
        x2="100%"
        y2="80%"
        stroke="url(#lineGradient)"
        strokeWidth="2"
        className="animate-team-connect"
        strokeDasharray="5,5"
      />
      <line
        x1="0%"
        y1="80%"
        x2="100%"
        y2="20%"
        stroke="url(#lineGradient)"
        strokeWidth="2"
        className="animate-team-connect"
        strokeDasharray="3,7"
        style={{ animationDelay: '0.5s' }}
      />
    </svg>
  );
};

// Floating particles for tech background
export const TechParticles = ({ count = 20, className = "" }: { count?: number; className?: string }) => {
  const particles = Array.from({ length: count }, (_, i) => ({
    id: i,
    delay: Math.random() * 4,
    duration: 3 + Math.random() * 2,
    size: 2 + Math.random() * 4,
    x: Math.random() * 100,
    y: Math.random() * 100
  }));

  return (
    <div className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}>
      {particles.map(particle => (
        <div
          key={particle.id}
          className="absolute rounded-full bg-primary/20 animate-data-flow"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            animationDelay: `${particle.delay}s`,
            animationDuration: `${particle.duration}s`
          }}
        />
      ))}
    </div>
  );
};

// Team collaboration icons with orbit animation
export const TeamOrbit = ({ 
  icons = ['👤', '👤', '👤', '👤'], 
  className = "" 
}: { 
  icons?: string[]; 
  className?: string; 
}) => {
  return (
    <div className={`relative w-32 h-32 ${className}`}>
      <div className="absolute inset-0 rounded-full border-2 border-primary/20"></div>
      {icons.map((icon, index) => (
        <div
          key={index}
          className="absolute w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-xs animate-collaboration-orbit"
          style={{
            animationDelay: `${index * 0.5}s`,
            top: '50%',
            left: '50%',
            marginTop: '-16px',
            marginLeft: '-16px'
          }}
        >
          {icon}
        </div>
      ))}
      <div className="absolute inset-4 bg-primary/5 rounded-full flex items-center justify-center animate-network-pulse">
        <div className="w-4 h-4 bg-primary rounded-full"></div>
      </div>
    </div>
  );
};

// Loading animation with tech theme
export const TechLoader = ({ 
  text = "Processing...", 
  className = "" 
}: { 
  text?: string; 
  className?: string; 
}) => {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex gap-1">
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className="w-2 h-2 bg-primary rounded-full animate-loading-dots"
            style={{ animationDelay: `${i * 0.2}s` }}
          />
        ))}
      </div>
      <span className="text-muted-foreground">{text}</span>
    </div>
  );
};